import { IAdvertiser, IAdvertiserDomain, IAdvertiserFormData } from "advertiser/model"
import { advertiserRPO } from "advertiser/repository"
import { advertiserActionCreator } from "advertiser/store/actions"
import { getStore } from "app/store/store"
import { normalizeSync } from "normalize-diacritics"
import { getUserAgencyId } from "user/tools/common"
import { validateAdvertiserFormData } from "./form"

export function getAdvertiserByName(advertiserName: string): IAdvertiser {
  const normalizedName = normalizeSync(advertiserName).toLowerCase()
  const advertiser = getStore()
    .getState()
    .advertiser.advertisers.find(a => a.normalizedName === normalizedName)
  if (!advertiser) {
    throw Error(`Advertiser name="${advertiserName}" not found`)
  }
  return advertiser
}

export const getAllAdvertiserDomain = (): Promise<IAdvertiserDomain[]> => advertiserRPO.getAllAdvertiserDomain()

export const getAdvertiserDomainInStoreById = (id: number): IAdvertiserDomain | undefined =>
  getStore()
    .getState()
    .advertiser.advertiserDomains.find(a => a.id === id)

export function createAdvertiser(data: IAdvertiserFormData): Promise<IAdvertiser> {
  return new Promise<IAdvertiser>((resolve, reject) => {
    const errors = validateAdvertiserFormData(data)
    if (Object.keys(errors).length > 0) {
      return reject(`Unable to create advertiser: ${JSON.stringify(errors)}`)
    }
    const { name, media_fee_percent } = data
    advertiserRPO
      .createAdvertiser({
        name,
        agency_id: getUserAgencyId(),
        media_fee_percent,
        language_id: undefined
      })
      .then(advertiser => {
        getStore().dispatch(advertiserActionCreator.add(advertiser))
        resolve(advertiser)
      })
      .catch(error => {
        console.error(error)
        reject("Unable to create new advertiser")
      })
  })
}

export function updateAdvertiser(advertiserId: number, data: IAdvertiserFormData): Promise<IAdvertiser> {
  return new Promise<IAdvertiser>((resolve, reject) => {
    const errors = validateAdvertiserFormData(data)
    if (Object.keys(errors).length > 0) {
      return reject(`Unable to update advertiser: ${JSON.stringify(errors)}`)
    }
    const { name, media_fee_percent } = data
    advertiserRPO
      .updateAdvertiser({
        id: advertiserId,
        name,
        media_fee_percent
      })
      .then(advertiser => {
        getStore().dispatch(advertiserActionCreator.update(advertiser))
        resolve(advertiser)
      })
      .catch(error => {
        console.error(error)
        reject("Unable to update advertiser")
      })
  })
}

export function getAdvertiserNameById(advertiserId: number): string {
  const advertiser = getStore()
    .getState()
    .advertiser.advertisers.find(a => a.id === advertiserId)
  return advertiser?.name || ""
}

export function getAdvertiserById(advertiserId: number): IAdvertiser {
  const advertiser = getStore()
    .getState()
    .advertiser.advertisers.find(a => a.id === advertiserId)
  if (!advertiser) {
    throw Error(`Advertiser id="${advertiserId}" not found`)
  }
  return advertiser
}

export function getAllAdvertiserIds(): number[] {
  return getStore()
    .getState()
    .advertiser.advertisers.map(a => a.id)
}

export const advertiserExistsByName = (name: string, id?: number): boolean => {
  if (!name || !name.trim()) {
    return false
  }
  const normalizedName = normalizeSync(name).toLowerCase()
  const fn =
    typeof id !== "number"
      ? (a: IAdvertiser) => a.normalizedName === normalizedName
      : (a: IAdvertiser) => a.normalizedName === normalizedName && a.id !== id
  return getStore().getState().advertiser.advertisers.find(fn) !== undefined
}

// string means an error
export async function getAdvertiserFromIdOrName(
  advertiserNameOrId: string | number | null
): Promise<IAdvertiser | string> {
  if (typeof advertiserNameOrId !== "number" && typeof advertiserNameOrId !== "string") {
    return "Advertiser is required"
  }
  let advertiser: IAdvertiser
  if (typeof advertiserNameOrId === "string") {
    if (advertiserNameOrId === "") {
      return "Invalid advertiser name"
    }
    if (advertiserExistsByName(advertiserNameOrId)) {
      advertiser = getAdvertiserByName(advertiserNameOrId)
    } else {
      advertiser = await createAdvertiser({ name: advertiserNameOrId, media_fee_percent: null })
    }
  } else {
    if (advertiserNameOrId < 0) {
      return "Invalid advertiser id, must be greater or equals than 0"
    }
    advertiser = getAdvertiserById(advertiserNameOrId)
  }
  return advertiser
}

export const searchAdvertiserDomainByName = async (name: string): Promise<IAdvertiserDomain[]> => {
  const { advertiserDomains } = getStore().getState().advertiser
  if (!name) {
    return advertiserDomains.slice()
  }
  const normalizedName = normalizeSync(name).toLowerCase()
  return advertiserDomains.filter(a => a.normalizedName.indexOf(normalizedName) !== -1)
}
